import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Eye, FileCode, FileSpreadsheet, ImageIcon, Loader2 } from 'lucide-react'

import { Alert, AlertDescription } from './ui/alert'
import { Badge } from './ui/badge'
import { Button } from './ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog'
import { Input } from './ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table'
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip'
import { FieldLabel } from './FieldLabel'
import { FileDropzone } from './FileDropzone'
import { HtmlPreviewThumbnail } from './HtmlPreviewThumbnail'
import { openPreviewInTab } from './CreativePreviewFrame'
import { LayoutsApi } from '@/services/sites/layoutsApi'
import { apiErrorDescription } from '@/services/core/apiError'
import {
  cleanUniqueId,
  dcoCreativeId,
  generateDcoCreative,
  htmlOnlyLayoutTag,
  imageDataUrl,
  previewDcoHtml,
  templateAdSize,
  templatePlaceholders,
  type DcoRow,
  type PlaceholderColumns,
} from '@/services/creatives/dcoTemplates'
import { readXlsxTable, type SheetTable } from '@/utils/xlsx'

/** Rows generated at the same time. */
const CONCURRENCY = 3
const NONE = '__none__'

type RowStatus =
  | { state: 'working' }
  | { state: 'done'; unreachableImages: string[] }
  | { state: 'error'; message: string }

interface DcoGenerateDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  lineItemId: string
  /** Ids of the line item's creatives, to tell new creatives from overwritten ones. */
  existingCreativeIds: Set<string>
}

/**
 * Maps each placeholder to a column: the column it was mapped to before (found
 * again by title, in case a new sheet moved it), otherwise the column with the
 * same name, ignoring case and spacing.
 */
function autoMap(
  placeholders: string[],
  headers: string[],
  prev: PlaceholderColumns,
  prevHeaders: string[]
): PlaceholderColumns {
  const key = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '')
  const next: PlaceholderColumns = {}
  for (const name of placeholders) {
    const kept = prev[name] !== undefined ? headers.indexOf(prevHeaders[prev[name]]) : -1
    const match = kept >= 0 ? kept : headers.findIndex((h) => key(h) === key(name))
    if (match >= 0) next[name] = match
  }
  return next
}

function findUniqueIdColumn(headers: string[]): number | null {
  const index = headers.findIndex((h) => h.toLowerCase().replace(/[^a-z]/g, '') === 'uniqueid')
  return index >= 0 ? index : null
}

const braces = (name: string) => `{{${name}}}`

/**
 * "DCO light": fills an HTML template's `{{placeholders}}` from the rows of a
 * spreadsheet and uploads each row as an HTML creative on the line item. A row's
 * creative id comes from its unique id, so uploading the sheet again updates
 * the same creatives.
 */
export function DcoGenerateDialog({
  open,
  onOpenChange,
  lineItemId,
  existingCreativeIds,
}: DcoGenerateDialogProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const [template, setTemplate] = useState<{ fileName: string; html: string } | null>(null)
  const [sheet, setSheet] = useState<{
    fileName: string
    table: SheetTable
    /** The sheet's pictures as data URLs, for the previews. */
    imageSrcs: Map<File, string>
  } | null>(null)
  const [readingSheet, setReadingSheet] = useState(false)
  const [layoutId, setLayoutId] = useState('')
  const [width, setWidth] = useState('')
  const [height, setHeight] = useState('')
  const [columns, setColumns] = useState<PlaceholderColumns>({})
  const [uniqueIdColumn, setUniqueIdColumn] = useState<number | null>(null)
  const [nameColumn, setNameColumn] = useState<number | null>(null)
  const [statuses, setStatuses] = useState<Record<string, RowStatus>>({})
  const [running, setRunning] = useState(false)

  const { data: layouts = [], isLoading: layoutsLoading } = useQuery({
    queryKey: ['layouts', 'active-with-components'],
    queryFn: () => LayoutsApi.getActiveWithComponents(),
    enabled: open,
  })
  const htmlLayouts = useMemo(() => layouts.filter((l) => htmlOnlyLayoutTag(l) !== null), [layouts])
  // A single candidate needs no choosing
  const effectiveLayoutId = layoutId || (htmlLayouts.length === 1 ? htmlLayouts[0].id : '')
  const selectedLayout = htmlLayouts.find((l) => l.id === effectiveLayoutId)

  const placeholders = useMemo(
    () => (template ? templatePlaceholders(template.html) : []),
    [template]
  )
  const headers = useMemo(() => sheet?.table.headers ?? [], [sheet])
  const columnLabel = (index: number | null | undefined) =>
    index === null || index === undefined
      ? ''
      : headers[index] || t('creatives.dco.columnNumber', { number: index + 1 })

  const templateBaseName = template?.fileName.replace(/\.[^.]+$/, '') ?? ''
  const { rows, missingIdCount, duplicateIds } = useMemo(() => {
    const result: DcoRow[] = []
    let missing = 0
    const seen = new Set<string>()
    const duplicates = new Set<string>()
    if (!sheet || uniqueIdColumn === null)
      return { rows: result, missingIdCount: 0, duplicateIds: duplicates }
    for (const cells of sheet.table.rows) {
      const uniqueId = cells[uniqueIdColumn]?.text.trim() ?? ''
      if (!cleanUniqueId(uniqueId)) {
        missing++
        continue
      }
      const creativeId = dcoCreativeId(lineItemId, uniqueId)
      if (seen.has(creativeId)) duplicates.add(cleanUniqueId(uniqueId))
      seen.add(creativeId)
      const name =
        (nameColumn !== null ? cells[nameColumn]?.text.trim() : '') ||
        `${templateBaseName} ${uniqueId}`.trim()
      result.push({ uniqueId, creativeId, name, cells })
    }
    return { rows: result, missingIdCount: missing, duplicateIds: duplicates }
  }, [sheet, uniqueIdColumn, nameColumn, lineItemId, templateBaseName])

  const unmapped = placeholders.filter((p) => columns[p] === undefined)
  const widthValue = parseInt(width, 10)
  const heightValue = parseInt(height, 10)
  const problems: string[] = []
  if (template && sheet) {
    if (!layoutsLoading && htmlLayouts.length === 0) problems.push(t('creatives.dco.noLayouts'))
    if (unmapped.length > 0)
      problems.push(t('creatives.dco.unmapped', { names: unmapped.map(braces).join(', ') }))
    if (uniqueIdColumn === null) problems.push(t('creatives.dco.noUniqueIdColumn'))
    if (missingIdCount > 0) problems.push(t('creatives.dco.missingIds', { count: missingIdCount }))
    if (duplicateIds.size > 0)
      problems.push(t('creatives.dco.duplicateIds', { ids: [...duplicateIds].join(', ') }))
    if (!(widthValue > 0 && heightValue > 0)) problems.push(t('creatives.dco.sizeRequired'))
  }
  const canGenerate =
    !!template &&
    !!sheet &&
    !!selectedLayout &&
    rows.length > 0 &&
    problems.length === 0 &&
    !running

  const handleTemplate = async (file: File) => {
    const html = await file.text()
    const found = templatePlaceholders(html)
    if (found.length === 0)
      toast.warning(t('creatives.dco.noPlaceholders', { example: braces('placeholder') }))
    const size = templateAdSize(html)
    if (size && !width && !height) {
      setWidth(String(size.width))
      setHeight(String(size.height))
    }
    setTemplate({ fileName: file.name, html })
    setColumns((prev) => autoMap(found, headers, prev, headers))
    setStatuses({})
  }

  const handleSheet = async (file: File) => {
    setReadingSheet(true)
    try {
      const table = await readXlsxTable(file)
      if (table.rows.length === 0) toast.warning(t('creatives.dco.emptySheet'))
      const images = [
        ...new Set(table.rows.flat().flatMap((cell) => (cell.image ? [cell.image] : []))),
      ]
      const imageSrcs = new Map(
        await Promise.all(images.map(async (image) => [image, await imageDataUrl(image)] as const))
      )
      setSheet({ fileName: file.name, table, imageSrcs })
      setColumns((prev) => autoMap(placeholders, table.headers, prev, headers))
      setUniqueIdColumn(findUniqueIdColumn(table.headers))
      setNameColumn(null)
      setStatuses({})
    } catch (e) {
      toast.error(t('creatives.dco.sheetFailed'), { description: (e as Error).message })
    } finally {
      setReadingSheet(false)
    }
  }

  // Each row's filled-in HTML, for the thumbnails and the open-in-tab preview
  const previews = useMemo(() => {
    const result = new Map<string, string>()
    if (!template || !sheet) return result
    for (const row of rows) {
      result.set(
        row.creativeId,
        previewDcoHtml(template.html, columns, row, (image) => sheet.imageSrcs.get(image) ?? '')
      )
    }
    return result
  }, [template, sheet, rows, columns])

  const generate = async () => {
    if (!template || !sheet || !selectedLayout) return
    const htmlTag = htmlOnlyLayoutTag(selectedLayout)!
    setRunning(true)
    setStatuses({})
    let failed = 0
    const queue = [...rows]
    const worker = async () => {
      for (let row = queue.shift(); row; row = queue.shift()) {
        const id = row.creativeId
        setStatuses((prev) => ({ ...prev, [id]: { state: 'working' } }))
        try {
          const result = await generateDcoCreative({
            lineItemId,
            layoutId: selectedLayout.id,
            htmlTag,
            width: widthValue,
            height: heightValue,
            template: template.html,
            headers,
            columns,
            row,
          })
          setStatuses((prev) => ({ ...prev, [id]: { state: 'done', ...result } }))
        } catch (e) {
          failed++
          setStatuses((prev) => ({
            ...prev,
            [id]: { state: 'error', message: apiErrorDescription(e) },
          }))
        }
      }
    }
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, rows.length) }, worker))
    setRunning(false)
    queryClient.invalidateQueries({ queryKey: ['lineitem-creatives', lineItemId] })
    queryClient.invalidateQueries({ queryKey: ['lineitem-preview-assets', lineItemId] })
    if (failed > 0) {
      toast.error(t('creatives.dco.someFailed', { count: failed, total: rows.length }))
    } else {
      toast.success(t('common.done'), {
        description: t('creatives.dco.allDone', { count: rows.length }),
      })
    }
  }

  const sample = (index: number | undefined) => {
    const cell = index !== undefined ? rows[0]?.cells[index] : undefined
    if (!cell) return null
    if (cell.image) {
      return (
        <span className="flex items-center gap-1.5 text-(--ad-text-muted)">
          <ImageIcon className="size-3.5" />
          {t('creatives.dco.picture')}
        </span>
      )
    }
    return <span className="line-clamp-1 text-(--ad-text-muted)">{cell.text}</span>
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !running && onOpenChange(next)}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-card sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>{t('creatives.dco.title')}</DialogTitle>
          <DialogDescription>
            {t('creatives.dco.description', { example: braces('placeholder') })}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-5">
          <div className="grid gap-3 sm:grid-cols-2">
            <FileDropzone
              accept=".html,.htm,text/html"
              label={t('creatives.dco.dropTemplate')}
              disabled={running}
              onFile={handleTemplate}
            >
              {template && (
                <>
                  <FileCode className="size-5 text-(--ad-text-muted)" />
                  <span className="font-medium">{template.fileName}</span>
                  <span className="text-xs text-(--ad-text-muted)">
                    {t('creatives.dco.placeholderCount', { count: placeholders.length })}
                  </span>
                </>
              )}
            </FileDropzone>
            <FileDropzone
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              label={t('creatives.dco.dropSheet')}
              busy={readingSheet}
              disabled={running}
              onFile={handleSheet}
            >
              {sheet && (
                <>
                  <FileSpreadsheet className="size-5 text-(--ad-text-muted)" />
                  <span className="font-medium">{sheet.fileName}</span>
                  <span className="text-xs text-(--ad-text-muted)">
                    {t('creatives.dco.sheetSummary', {
                      rows: sheet.table.rows.length,
                      columns: headers.length,
                    })}
                  </span>
                </>
              )}
            </FileDropzone>
          </div>

          {template && sheet && (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <FieldLabel tooltip={t('creatives.dco.layoutHelp')}>
                    {t('creatives.layout')}
                  </FieldLabel>
                  <Select
                    label={t('creatives.layout')}
                    value={effectiveLayoutId}
                    onValueChange={setLayoutId}
                    disabled={running || htmlLayouts.length === 0}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('creatives.selectLayout')}>
                        {selectedLayout?.name ?? t('creatives.selectLayout')}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {htmlLayouts.map((l) => (
                        <SelectItem key={l.id} value={l.id}>
                          {l.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex flex-col gap-1.5">
                    <FieldLabel htmlFor="dco-width" tooltip={t('creatives.dco.sizeHelp')}>
                      {t('creatives.width')}
                    </FieldLabel>
                    <Input
                      id="dco-width"
                      inputMode="numeric"
                      value={width}
                      disabled={running}
                      onChange={(e) => setWidth(e.target.value.replace(/\D/g, ''))}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <FieldLabel htmlFor="dco-height">{t('creatives.height')}</FieldLabel>
                    <Input
                      id="dco-height"
                      inputMode="numeric"
                      value={height}
                      disabled={running}
                      onChange={(e) => setHeight(e.target.value.replace(/\D/g, ''))}
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-1.5">
                  <FieldLabel tooltip={t('creatives.dco.uniqueIdHelp')}>
                    {t('creatives.dco.uniqueIdColumn')}
                  </FieldLabel>
                  <Select
                    label={t('creatives.dco.uniqueIdColumn')}
                    value={uniqueIdColumn === null ? '' : String(uniqueIdColumn)}
                    onValueChange={(v) => setUniqueIdColumn(Number(v))}
                    disabled={running}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('creatives.dco.selectColumn')}>
                        {uniqueIdColumn === null
                          ? t('creatives.dco.selectColumn')
                          : columnLabel(uniqueIdColumn)}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {headers.map((_, i) => (
                        <SelectItem key={i} value={String(i)}>
                          {columnLabel(i)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col gap-1.5">
                  <FieldLabel tooltip={t('creatives.dco.nameColumnHelp')}>
                    {t('creatives.dco.nameColumn')}
                  </FieldLabel>
                  <Select
                    label={t('creatives.dco.nameColumn')}
                    value={nameColumn === null ? NONE : String(nameColumn)}
                    onValueChange={(v) => setNameColumn(v === NONE ? null : Number(v))}
                    disabled={running}
                  >
                    <SelectTrigger>
                      <SelectValue>
                        {nameColumn === null
                          ? t('creatives.dco.defaultName')
                          : columnLabel(nameColumn)}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>{t('creatives.dco.defaultName')}</SelectItem>
                      {headers.map((_, i) => (
                        <SelectItem key={i} value={String(i)}>
                          {columnLabel(i)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {placeholders.length > 0 && (
                <div className="flex flex-col gap-2">
                  <h3 className="text-sm font-medium">{t('creatives.dco.mapping')}</h3>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('creatives.dco.placeholder')}</TableHead>
                        <TableHead>{t('creatives.dco.column')}</TableHead>
                        <TableHead>{t('creatives.dco.firstRow')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {placeholders.map((name) => (
                        <TableRow key={name}>
                          <TableCell className="font-mono text-xs">{braces(name)}</TableCell>
                          <TableCell className="w-64">
                            <Select
                              label={braces(name)}
                              value={columns[name] === undefined ? '' : String(columns[name])}
                              onValueChange={(v) =>
                                setColumns((prev) => ({ ...prev, [name]: Number(v) }))
                              }
                              disabled={running}
                            >
                              <SelectTrigger size="sm" aria-invalid={columns[name] === undefined}>
                                <SelectValue placeholder={t('creatives.dco.selectColumn')}>
                                  {columns[name] === undefined
                                    ? t('creatives.dco.selectColumn')
                                    : columnLabel(columns[name])}
                                </SelectValue>
                              </SelectTrigger>
                              <SelectContent>
                                {headers.map((_, i) => (
                                  <SelectItem key={i} value={String(i)}>
                                    {columnLabel(i)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="max-w-64">{sample(columns[name])}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {problems.length > 0 && (
                <Alert variant="warning">
                  <AlertDescription>
                    <ul className="list-disc pl-4">
                      {problems.map((p) => (
                        <li key={p}>{p}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              {rows.length > 0 && (
                <div className="flex flex-col gap-2">
                  <h3 className="text-sm font-medium">
                    {t('creatives.creativeCount', { count: rows.length })}
                  </h3>
                  <div className="max-h-80 overflow-y-auto rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('creatives.dco.uniqueId')}</TableHead>
                          <TableHead>{t('creatives.preview')}</TableHead>
                          <TableHead>{t('creatives.dco.creativeId')}</TableHead>
                          <TableHead>{t('common.name')}</TableHead>
                          <TableHead>{t('common.status')}</TableHead>
                          <TableHead />
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {rows.map((row) => (
                          <TableRow key={row.creativeId}>
                            <TableCell>{row.uniqueId}</TableCell>
                            <TableCell className="py-1.5">
                              <HtmlPreviewThumbnail
                                html={previews.get(row.creativeId) ?? ''}
                                width={widthValue}
                                height={heightValue}
                              />
                            </TableCell>
                            <TableCell className="font-mono text-xs">{row.creativeId}</TableCell>
                            <TableCell className="max-w-56 truncate">{row.name}</TableCell>
                            <TableCell>
                              <RowStatusBadge
                                status={statuses[row.creativeId]}
                                exists={existingCreativeIds.has(row.creativeId)}
                              />
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                aria-label={t('creatives.preview')}
                                onClick={() => openPreviewInTab(previews.get(row.creativeId) ?? '')}
                              >
                                <Eye className="size-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            disabled={running}
            onClick={() => onOpenChange(false)}
          >
            {t('common.close')}
          </Button>
          <Button type="button" disabled={!canGenerate} onClick={generate}>
            {running && <Loader2 className="size-4 animate-spin" />}
            {t('creatives.dco.generate', { count: rows.length })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function RowStatusBadge({ status, exists }: { status?: RowStatus; exists: boolean }) {
  const { t } = useTranslation()
  if (!status) {
    return (
      <Badge variant={exists ? 'outline' : 'blue'}>
        {exists ? t('creatives.dco.willUpdate') : t('creatives.dco.willCreate')}
      </Badge>
    )
  }
  if (status.state === 'working') {
    return <Loader2 className="size-4 animate-spin text-(--ad-text-muted)" />
  }
  const tip =
    status.state === 'error'
      ? status.message
      : status.unreachableImages.length > 0
        ? t('creatives.dco.unreachableImages', { urls: status.unreachableImages.join('\n') })
        : null
  const badge =
    status.state === 'error' ? (
      <Badge variant="destructive">{t('creatives.dco.failed')}</Badge>
    ) : (
      <Badge variant={tip ? 'warning' : 'success'}>{t('creatives.dco.uploaded')}</Badge>
    )
  if (!tip) return badge
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="cursor-help">{badge}</span>
      </TooltipTrigger>
      <TooltipContent className="max-w-sm whitespace-pre-line">{tip}</TooltipContent>
    </Tooltip>
  )
}
